/**
 * CNPJ — validação por dígito verificador, aceitando o formato ALFANUMÉRICO.
 *
 * Story 50.1. O CNPJ alfanumérico entrou em vigor em 31/07/2026 (Instrução Normativa RFB
 * nº 2.229/2024, detalhada na Nota Técnica COCAD/SUARA/RFB nº 49/2024). Antes disto o
 * sistema recusava esse CNPJ em duas camadas — a máscara apagava as letras enquanto a
 * pessoa digitava, e o servidor contava 14 dígitos —, então clínica aberta a partir de
 * 01/08/2026 simplesmente não conseguia se cadastrar.
 *
 * ESTRUTURA (12 + 2):
 *   posições 1–8   raiz ............... alfanumérico [A-Z0-9]
 *   posições 9–12  estabelecimento .... alfanumérico [A-Z0-9]
 *   posições 13–14 dígitos verificadores  SOMENTE numérico
 *
 * O CÁLCULO continua sendo módulo 11 com os pesos de sempre. A única mudança da norma é
 * de onde vem o valor de cada caractere: em vez do dígito, usa-se o **código ASCII menos
 * 48**. Para '0'–'9' isso devolve o próprio dígito (48−48=0 … 57−48=9), e é por isso que
 * CNPJ numérico continua valendo sem exceção no código — ele é um caso particular da
 * mesma conta, não um ramo à parte. Para letras, 'A' vale 17 e 'E' vale 21.
 *
 * POR QUE CAIXA ALTA IMPORTA: 'a' é 97, então 97−48 = 49 — número completamente diferente
 * de 17. Minúscula não é variação cosmética, é outro valor. Por isso o normalizador sobe
 * a caixa antes de qualquer conta.
 *
 * VERIFICAÇÃO DO ALGORITMO (2026-08-24): não foi possível obter da Receita um documento
 * com lista publicada de exemplos válidos — a Nota Técnica descreve o mecanismo, não
 * publica vetores. O caminho usado foi o inverso e está nas specs: implementar pela norma
 * e conferir se ESTA função reproduz os dígitos verificadores de exemplos calculados por
 * terceiros independentes. Reproduziu os quatro (dois alfanuméricos, dois numéricos, um
 * deles CNPJ real). Reproduzir DV de terceiro não é aceitar a palavra dele — é comparar
 * dois cálculos independentes sobre a mesma norma.
 */
/** Pesos do módulo 11 do CNPJ — os mesmos de sempre; a norma não os alterou. */
const PESOS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const BASE_LEN = 12;
const CNPJ_LEN = 14;
/** Valor do caractere segundo a norma: código ASCII − 48. */
function valorDoCaractere(c) {
    return c.charCodeAt(0) - 48;
}
/**
 * Tira máscara e normaliza a caixa. NÃO valida — só deixa o valor na forma canônica.
 * Aceita o que a pessoa digitou com pontuação (`33.EMA.SAM/E007-81`) ou sem.
 */
export function normalizeCnpj(input) {
    if (!input)
        return '';
    return String(input)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, CNPJ_LEN);
}
function calcularDv(valores, pesos) {
    const soma = valores.reduce((acc, v, i) => acc + v * pesos[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
}
/**
 * Calcula os dois dígitos verificadores a partir das 12 primeiras posições.
 * Devolve string de 2 dígitos, ou `''` se a base for inválida.
 */
export function cnpjCheckDigits(base) {
    const b = normalizeCnpj(base).slice(0, BASE_LEN);
    if (b.length !== BASE_LEN)
        return '';
    const valores = [...b].map(valorDoCaractere);
    const dv1 = calcularDv(valores, PESOS_DV1);
    const dv2 = calcularDv([...valores, dv1], PESOS_DV2);
    return `${dv1}${dv2}`;
}
/**
 * O CNPJ é estruturalmente válido?
 *
 * Valida por DÍGITO VERIFICADOR, não por formato — regex de formato aceita CNPJ falso, e
 * era esse o buraco que a story 50.1 fecha. Aceita numérico e alfanumérico pela mesma conta.
 */
export function isValidCnpj(input) {
    const cnpj = normalizeCnpj(input);
    if (cnpj.length !== CNPJ_LEN)
        return false;
    // Os dois últimos são SEMPRE numéricos, mesmo no formato alfanumérico.
    if (!/^\d{2}$/.test(cnpj.slice(BASE_LEN)))
        return false;
    // Repetição total (00000000000000, AAAAAAAAAAAA00…) satisfaz o módulo 11 por acidente
    // aritmético e é recusada em toda implementação de CPF/CNPJ pelo mesmo motivo.
    if (/^(.)\1{13}$/.test(cnpj))
        return false;
    return cnpjCheckDigits(cnpj.slice(0, BASE_LEN)) === cnpj.slice(BASE_LEN);
}
/**
 * Formata no agrupamento oficial `00.000.000/0000-00`, preservando letras.
 * Máscara PROGRESSIVA: serve para formatar enquanto a pessoa digita.
 */
export function formatCnpj(input) {
    const c = normalizeCnpj(input);
    if (c.length <= 2)
        return c;
    if (c.length <= 5)
        return `${c.slice(0, 2)}.${c.slice(2)}`;
    if (c.length <= 8)
        return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5)}`;
    if (c.length <= 12)
        return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8)}`;
    return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}
export function cnpjRejeicao(input) {
    const cnpj = normalizeCnpj(input);
    if (cnpj.length !== CNPJ_LEN || !/^\d{2}$/.test(cnpj.slice(BASE_LEN)))
        return 'comprimento';
    return isValidCnpj(cnpj) ? null : 'digito-verificador';
}
export const CNPJ_MENSAGEM = {
    comprimento: 'CNPJ incompleto. São 14 posições: 12 de letras ou números e 2 dígitos verificadores no final.',
    'digito-verificador': 'CNPJ inválido — os dígitos verificadores não conferem. Confira se digitou corretamente.',
};
//# sourceMappingURL=cnpj.js.map